import { type LaunchParams, POST_TAG_NAMES, type PostTag } from '@mogzo/shared';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router';
import { type Author, type PlayableBuild, type Post, timeAgo } from '../../lib/communities';
import { SplashCard } from '../play/SplashCard';
import { Avatar } from './Avatar';
import { GgButton } from './GgButton';
import { PostImages } from './PostImages';

const TAG_COLOURS: Record<PostTag, string> = {
  bug: '#fde3e1',
  idea: '#e2f3dc',
  question: '#dcecf7',
  other: '#f3f1f8',
};

export function TagChip({ tag }: { tag: PostTag }) {
  return (
    <span
      style={{ background: TAG_COLOURS[tag] }}
      className="rounded-full px-2.5 py-[3px] text-xs font-bold text-ink"
    >
      {POST_TAG_NAMES[tag]}
    </span>
  );
}

export function DeveloperBadge({ label = 'Developer' }: { label?: string }) {
  return (
    <span className="rounded-full bg-ink px-2 py-0.5 text-[11px] font-bold text-white">
      {label}
    </span>
  );
}

/**
 * The badge after a name: the developer in a test community; managers in a team community
 * (its maker included) and test managers.
 */
export function RoleBadge({ role, team }: { role: Author['role']; team: boolean }) {
  if (role === 'owner') return <DeveloperBadge label={team ? 'Manager' : 'Developer'} />;
  if (role === 'manager') return <DeveloperBadge label="Manager" />;
  return null;
}

export function AuthorName({ author, team = false }: { author: Author; team?: boolean }) {
  return (
    <>
      <strong>{author.name}</strong>
      <RoleBadge role={author.role} team={team} />
    </>
  );
}

function OutlineIcon({ children }: { children: ReactNode }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export const actionClass =
  'inline-flex min-h-10 items-center gap-2 rounded-full px-3.5 text-sm font-bold text-ink hover:bg-field';

export function commentsLabel(count: number): string {
  return count ? `${count} comment${count === 1 ? '' : 's'}` : 'Comment';
}

/** Copies the post's address. Only members can open it, so sharing stays inside the test. */
export function ShareButton({ path }: { path: string }) {
  const [copied, setCopied] = useState(false);
  async function share() {
    try {
      await navigator.clipboard.writeText(new URL(path, window.location.origin).toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked: nothing to do.
    }
  }
  return (
    <button type="button" onClick={share} className={actionClass}>
      <OutlineIcon>
        <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
        <path d="M12 3v12M7 8l5-5 5 5" />
      </OutlineIcon>
      <span aria-live="polite">{copied ? 'Link copied' : 'Share'}</span>
    </button>
  );
}

export function CommentIcon() {
  return (
    <OutlineIcon>
      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
    </OutlineIcon>
  );
}

/** One post in a community feed: who posted, what they said, the game's splash for builds. */
export function PostCard({
  post,
  community,
  onLaunch,
}: {
  post: Post;
  /** `path` is where the community lives; the game, for test communities. */
  community: { name: string; path: string; projectId: string | null; developerName: string | null };
  onLaunch: (build: PlayableBuild, params: LaunchParams) => void;
}) {
  const path = `${community.path}/posts/${post.id}`;
  const game =
    community.projectId && community.developerName
      ? {
          name: community.name,
          projectId: community.projectId,
          developerName: community.developerName,
        }
      : null;
  const playUrl = post.kind === 'build' ? post.build?.playUrl : null;
  return (
    <article className="overflow-hidden rounded-[18px] border border-line bg-card">
      <div className="flex items-center gap-2.5 px-4 pt-4 sm:px-[18px]">
        <Avatar name={post.author.name} />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="flex flex-wrap items-center gap-1.5">
            <AuthorName author={post.author} team={!community.projectId} />
          </div>
          <span className="text-[13px] text-muted">
            {timeAgo(post.createdAt)}
            {post.kind === 'text' && post.build && ` · on build #${post.build.number}`}
          </span>
        </div>
        {post.pinned && <span className="text-xs font-bold text-muted">Pinned</span>}
        {post.tag && <TagChip tag={post.tag} />}
      </div>

      {(post.title || post.body) && (
        <Link to={path} className="block px-4 pt-2.5 sm:px-[18px]">
          {post.title && <h2 className="mb-1 text-[19px] leading-snug font-bold">{post.title}</h2>}
          <p className="line-clamp-5 leading-relaxed whitespace-pre-line text-plum">{post.body}</p>
        </Link>
      )}

      {post.images.length > 0 && (
        <div className="mx-4 mt-3 sm:mx-[18px]">
          <PostImages images={post.images} author={post.author.name} to={path} />
        </div>
      )}

      {post.build && playUrl && game && (
        <div className="mx-4 mt-3.5 sm:mx-[18px]">
          <BuildSplash build={{ ...post.build, playUrl }} community={game} onLaunch={onLaunch} />
        </div>
      )}

      {post.developerReply && community.developerName && (
        <Link
          to={path}
          className="mx-4 mt-3 flex items-start gap-2.5 rounded-xl bg-canvas px-3 py-2.5 sm:mx-[18px]"
        >
          <Avatar name={community.developerName} size="xs" />
          <span className="line-clamp-2 text-sm leading-snug">
            <strong>{community.developerName}</strong> {post.developerReply}
          </span>
        </Link>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1 px-3 py-2.5">
        <GgButton target="post" id={post.id} count={post.ggCount} on={post.viewerGg} />
        <Link to={path} className={actionClass}>
          <CommentIcon />
          {commentsLabel(post.commentCount)}
        </Link>
        <ShareButton path={path} />
      </div>
    </article>
  );
}

/** A build post's game: its splash, launching that build. */
export function BuildSplash({
  build,
  community,
  onLaunch,
}: {
  build: PlayableBuild;
  community: { name: string; projectId: string; developerName: string };
  onLaunch: (build: PlayableBuild, params: LaunchParams) => void;
}) {
  return (
    <SplashCard
      name={community.name}
      developerName={community.developerName}
      build={build}
      projectId={community.projectId}
      onLaunch={(params) => onLaunch(build, params)}
    />
  );
}
